// ✅ src/pages/VehicleV2/ProgressStatus.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaInfoCircle } from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ backend mount
const API_BASE = "https://nissi-india.com/api/progressStatus";

/* helpers */
const toStr = (v) => String(v ?? "").trim();

export default function ProgressStatus() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend-matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const vehicleId = toStr(sp.get("vehicleId") || "");
  const branchId = toStr(sp.get("branchId") || sessionBranchId || "");
  const status = toStr(sp.get("status") || "Breakdown");

  const [loading, setLoading] = useState(false);

  // table1: active list
  const [docRows, setDocRows] = useState([]);

  // table2: solved/history list
  const [progRows, setProgRows] = useState([]);

  const tableWrapClass = `rounded-xl shadow-sm border overflow-hidden ${
    isDark ? "border-gray-700" : "border-gray-200"
  }`;

  const loadData = async () => {
    if (!vehicleId) {
      toast.error("Missing vehicleId in URL");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("vehicleId", vehicleId);
      if (branchId) params.set("branchId", branchId);
      params.set("status", status);

      const { data } = await axios.get(`${API_BASE}?${params.toString()}`, {
        headers: apiHeaders,
      });

      if (!data?.success) throw new Error(data?.message || "Failed");

      const a = data.docRows || data.rows1 || [];
      const b = data.progRows || data.rows2 || [];

      setDocRows(
        a.map((r, idx) => ({
          ...r,
          _checked: Boolean(r._checked) || false,
          _sno: r.sno ?? idx + 1,
        }))
      );

      setProgRows(
        b.map((r, idx) => ({
          ...r,
          _sno: r.sno ?? idx + 1,
        }))
      );
    } catch (err) {
      setDocRows([]);
      setProgRows([]);
      toast.error(err.message || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, branchId, status, apiHeaders]);

  const toggleChecked = (idx) => {
    setDocRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, _checked: !r._checked } : r))
    );
  };

  const handleSaveCompleted = async () => {
    const selected = docRows.filter((r) => r._checked);

    if (!selected.length) {
      toast.info("Please select at least one row");
      return;
    }

    try {
      const payload = {
        vehicleId,
        branchId,
        status,
        ids: selected
          .map(
            (r) =>
              r.fld_vehic_progs_id ??
              r.progsId ??
              r.id ??
              r.fld_id ??
              null
          )
          .filter(Boolean),
      };

      if (!payload.ids.length) {
        toast.error("Selected rows do not have IDs");
        return;
      }

      const { data } = await axios.post(`${API_BASE}/complete`, payload, {
        headers: apiHeaders,
      });

      if (!data?.success) throw new Error(data?.message || "Save failed");

      toast.success("Completed saved");
      loadData();
    } catch (err) {
      toast.error(err.message || "Save failed");
    }
  };

  const docList = useMemo(() => {
    return (docRows || []).map((r) => ({
      id: r.fld_vehic_progs_id ?? r.progsId ?? r.id ?? "",
      sno: r._sno,
      date: r.fld_progressentrydate ?? r.date ?? "",
      remarks: r.fld_remarks ?? r.remarks ?? "",
      checked: r._checked,
      _row: r,
    }));
  }, [docRows]);

  const progList = useMemo(() => {
    return (progRows || []).map((r) => ({
      sno: r._sno,
      date: r.fld_date ?? r.date ?? "",
      solvedDate: r.fld_createdon ?? r.solvedDate ?? "",
      remarks: r.fld_remarks ?? r.remarks ?? "",
      _row: r,
    }));
  }, [progRows]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PROGRESS STATUS UPDATE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <div className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Progress Status Update
              </div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {status || "Breakdown"}
                {loading ? " • Loading..." : ""}
              </div>
              <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                vehicleId: {vehicleId || "-"} {branchId ? `• branchId: ${branchId}` : ""}
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleSaveCompleted}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-3 rounded-lg font-semibold shadow-md`}
                disabled={loading}
              >
                <FaSave /> Save Completed
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-6 py-3 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft /> Back
              </button>
            </div>
          </div>

          {/* TABLE 1 */}
          <div className="mb-8">
            <div className={`mb-3 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
              {status}
            </div>

            <div className={tableWrapClass}>
              {docList.length === 0 ? (
                <div className="text-center py-10">
                  <FaInfoCircle className="inline w-8 h-8 text-gray-400 mb-2" />
                  <div className={`${isDark ? "text-gray-200" : "text-gray-800"} font-semibold`}>
                    No Records Found
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${buttonClass} text-white`}>
                      <tr>
                        <th className="px-4 py-3 text-center w-16">S.No</th>
                        <th className="px-4 py-3 text-center">Date</th>
                        <th className="px-4 py-3 text-center">Remarks</th>
                        <th className="px-4 py-3 text-center w-32">Completed</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {docList.map((r, idx) => (
                        <tr
                          key={`${r.id || "row"}-${idx}`}
                          className={`${isDark ? "bg-gray-800" : "bg-white"} ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3 text-center">{r.sno}</td>
                          <td className="px-4 py-3 text-center">{r.date}</td>
                          <td className="px-4 py-3 text-center">{r.remarks}</td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!docRows[idx]?._checked}
                              onChange={() => toggleChecked(idx)}
                              className="w-5 h-5 cursor-pointer"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* TABLE 2 */}
          <div>
            <div className={tableWrapClass}>
              {progList.length === 0 ? (
                <div className="text-center py-10">
                  <FaInfoCircle className="inline w-8 h-8 text-gray-400 mb-2" />
                  <div className={`${isDark ? "text-gray-200" : "text-gray-800"} font-semibold`}>
                    No Records Found
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${buttonClass} text-white`}>
                      <tr>
                        <th className="px-4 py-3 text-center w-16">S.No</th>
                        <th className="px-4 py-3 text-center">Date</th>
                        <th className="px-4 py-3 text-center">Solved_Date</th>
                        <th className="px-4 py-3 text-center">Remarks</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {progList.map((r, idx) => (
                        <tr
                          key={`prog-${idx}`}
                          className={`${isDark ? "bg-gray-800" : "bg-white"} ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3 text-center">{r.sno}</td>
                          <td className="px-4 py-3 text-center">{r.date}</td>
                          <td className="px-4 py-3 text-center">{r.solvedDate}</td>
                          <td className="px-4 py-3 text-center">{r.remarks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}